/**
 * Sort keys for summary-table columns against a saved `accountSummary.columnOrder`.
 *
 * Column ids follow the summary-table field convention `${columnGroupId}.${value}.`; a column with no
 * value segment in the current slice (per-group Totals, Empty Values) is keyed `${columnGroupId}.Empty Values.`.
 * Ids without a `.` — the auto group column, placeholders — belong to no column group. The reporting-app
 * grid, the chart series order, the service's exported tables and the embeds all resolve their keys here,
 * so every surface lays a report's columns out the same way.
 */

export type ColumnSortKeys = Map<string, number | undefined>;

export function getColumnGroupIdFromColumnId(columnId: string): string {
  return columnId.split('.')[0];
}

function isColumnGroupColumnId(columnId: string): boolean {
  return getColumnGroupIdFromColumnId(columnId) !== columnId;
}

/**
 * The saved position of one column: its own key, else the position of the first saved key sharing its
 * column-group prefix, else `undefined`. The prefix fallback keeps a column whose value segment changed
 * between saves — "Empty Values" once data appears, or the reverse (BUD-8668) — in its group's slot. It
 * is only sound for a group with a single live column; {@link resolveColumnSortKeys} overrides it for the
 * stale groups {@link findStaleColumnGroupIds} reports.
 */
export function getSavedColumnSortKey(columnOrder: Record<string, number>, columnId: string): number | undefined {
  if (columnId in columnOrder) {
    return columnOrder[columnId];
  }
  const prefix = getColumnGroupIdFromColumnId(columnId);
  const prefixKey = Object.keys(columnOrder).find((key) => key.startsWith(prefix));
  return prefixKey === undefined ? undefined : columnOrder[prefixKey];
}

function groupColumnIdsByColumnGroup(columnIds: readonly string[]): Map<string, string[]> {
  const idsByGroup = new Map<string, string[]>();
  for (const columnId of columnIds) {
    if (!isColumnGroupColumnId(columnId)) continue;
    const groupId = getColumnGroupIdFromColumnId(columnId);
    idsByGroup.set(groupId, [...(idsByGroup.get(groupId) ?? []), columnId]);
  }
  return idsByGroup;
}

/**
 * Column groups whose saved order no longer describes their live columns: more than one live column, and
 * at least one of them has no saved key. A dimension's value set shifting under a saved order causes this —
 * a date filter moving from calendar year 2026 to fiscal year 2027 keeps Jul–Dec 2026 but replaces
 * Jan–Jun 2026 with Jan–Jun 2027. The saved positions cannot be assigned to such a group's columns, so
 * {@link resolveColumnSortKeys} lays the whole group out in definition order instead. A group with a single
 * live column is never stale: {@link getSavedColumnSortKey} reuses the group's saved slot for it.
 */
export function findStaleColumnGroupIds(
  columnOrder: Record<string, number>,
  columnIds: readonly string[]
): Set<string> {
  const staleGroupIds = new Set<string>();
  for (const [groupId, groupColumnIds] of groupColumnIdsByColumnGroup(columnIds)) {
    const hasUnsavedColumn = groupColumnIds.some((columnId) => !(columnId in columnOrder));
    if (groupColumnIds.length > 1 && hasUnsavedColumn) {
      staleGroupIds.add(groupId);
    }
  }
  return staleGroupIds;
}

function getSavedGroupAnchor(columnOrder: Record<string, number>, groupId: string): number | undefined {
  const groupOrders = Object.entries(columnOrder)
    .filter(([key]) => key.startsWith(`${groupId}.`))
    .map(([, order]) => order);
  return groupOrders.length > 0 ? Math.min(...groupOrders) : undefined;
}

/**
 * Keys of a stale group's columns sit strictly between the group's anchor (its lowest saved position) and
 * `anchor + 1`. Saved positions are not unique, so a plain `anchor + index / size` could tie the group's
 * first column with an unrelated column saved at the same integer and let that column split the group.
 */
export function getStaleGroupSortKey(anchor: number, index: number, groupSize: number): number {
  return anchor + (index + 1) / (groupSize + 1);
}

function orderLiveColumnIds(columnIds: readonly string[], definitionOrderIds: readonly string[]): string[] {
  const liveIds = new Set(columnIds);
  return [...new Set([...definitionOrderIds.filter((id) => liveIds.has(id)), ...columnIds])];
}

/**
 * Resolves a sort key for every live column. Columns of a stale group (see {@link findStaleColumnGroupIds})
 * follow their order in `definitionOrderIds` — the server's column order — packed just after the group's
 * lowest saved position, so the group keeps its place among the other groups. A stale group with no saved
 * position at all gets no key and sorts last like any unsaved column ({@link compareColumnSortKeys}).
 * @param definitionOrderIds live column ids in column-definition order; defaults to `columnIds`
 */
export function resolveColumnSortKeys(
  columnOrder: Record<string, number>,
  columnIds: readonly string[],
  definitionOrderIds: readonly string[] = columnIds
): ColumnSortKeys {
  const sortKeys: ColumnSortKeys = new Map(columnIds.map((id) => [id, getSavedColumnSortKey(columnOrder, id)]));
  const liveIdsInDefinitionOrder = orderLiveColumnIds(columnIds, definitionOrderIds);

  for (const groupId of findStaleColumnGroupIds(columnOrder, columnIds)) {
    const anchor = getSavedGroupAnchor(columnOrder, groupId);
    const groupColumnIds = liveIdsInDefinitionOrder.filter((id) => getColumnGroupIdFromColumnId(id) === groupId);
    groupColumnIds.forEach((id, index) => {
      sortKeys.set(id, anchor === undefined ? undefined : getStaleGroupSortKey(anchor, index, groupColumnIds.length));
    });
  }
  return sortKeys;
}

/** Columns without a key go last; equal keys keep their input order under a stable sort. */
export function compareColumnSortKeys(a: number | undefined, b: number | undefined): number {
  if (a === undefined && b === undefined) return 0;
  if (a === undefined) return 1;
  if (b === undefined) return -1;
  return a - b;
}
